import { MousePointerClick, Pencil, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { type ReportPanel } from "../api";
import { PanelView } from "./PanelView";

/**
 * A saved report panel with card chrome (title, type/drill-down badges, edit +
 * remove), shown in the builder's saved-panels strip. The chart/query rendering
 * lives in {@link PanelView}; the dashboard grid renders the same PanelView
 * without this chrome.
 */
export function PanelCard({
  panel,
  onEdit,
  onRemove,
  active,
}: {
  panel: ReportPanel;
  /** Load this panel back into the editor to revise it. */
  onEdit?: () => void;
  onRemove?: () => void;
  /** This panel is the one currently open in the editor. */
  active?: boolean;
}) {
  const drilldown = panel.viz.drilldown;
  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-3 transition-colors",
        active ? "border-brand ring-1 ring-brand/40" : "border-border",
      )}
    >
      <div className="mb-2 flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">
          {panel.title || "Untitled panel"}
        </span>
        {drilldown && (
          <Badge variant="info" title={`Clicks open ${drilldown.route}`}>
            <MousePointerClick className="mr-1 h-3 w-3" />
            Drill-down
          </Badge>
        )}
        <Badge variant="secondary">{panel.type}</Badge>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="text-muted-foreground hover:text-brand"
            title="Edit panel"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-muted-foreground hover:text-danger"
            title="Remove panel"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PanelView panel={panel} className="h-52" />
    </div>
  );
}
